import { useEffect, useRef, useState } from "react";
import { Box, Button, TextField, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import ImageCropper from "../../../components/ImageCropper";
import Loader from "../../../components/Loader";
import { updateFields } from "../../../redux/profileSlice";
import {
  ACCEPTED_IMAGE_TYPES,
  MAX_IMAGE_BYTES,
} from "../../../helpers/imageUpload";
import { uploadProfilePhoto } from "../../../helpers/uploadProfilePhoto";
import { splitFullName } from "../../../helpers/profileName";

const inputSx = {
  marginBottom: "12px",
  borderRadius: "50px",
  backgroundColor: "#F2F7FB",
  "& .MuiInputBase-input": { color: "#2A4876" },
};

const V2ProfileBasics = ({ localProfile }) => {
  const profile = localProfile?.profile ?? {};
  const updatedFields = useSelector(
    (state) => state.profileSlice?.updatedFields,
  );
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const [cropSelection, setCropSelection] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    return () => {
      if (cropSelection?.source) URL.revokeObjectURL(cropSelection.source);
    };
  }, [cropSelection]);

  const firstName = updatedFields?.firstName ?? profile.firstName ?? "";
  const lastName = updatedFields?.lastName ?? profile.lastName ?? "";
  const [fullName, setFullName] = useState(() =>
    `${firstName} ${lastName}`.trim(),
  );

  const handleNameChange = (event) => {
    const value = event.target.value;
    setFullName(value);
    const { firstName, lastName } = splitFullName(value);
    dispatch(updateFields({ field: "firstName", value: firstName }));
    dispatch(updateFields({ field: "lastName", value: lastName }));
  };

  const selectPhoto = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      setError("Choose a JPG, PNG, GIF or WebP image.");
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      setError("That image is larger than 10 MB.");
      return;
    }
    setError("");
    setCropSelection({ file, source: URL.createObjectURL(file) });
  };

  const uploadPhoto = async (croppedFile) => {
    if (!cropSelection) return;
    const file = new File([croppedFile], cropSelection.file.name, {
      type: croppedFile.type || cropSelection.file.type,
      lastModified: cropSelection.file.lastModified,
    });
    setCropSelection(null);
    setLoading(true);
    setError("");
    try {
      const url = await uploadProfilePhoto(file);
      setPreview(url);
      dispatch(updateFields({ field: "photo", value: url }));
    } catch (uploadError) {
      setError(uploadError?.message ?? "That photo could not be uploaded.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {loading && <Loader />}
      {error && (
        <Typography role="alert" sx={{ mb: 1.5, color: "#B3312C" }}>
          {error}
        </Typography>
      )}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <Box
          sx={{
            width: { xs: 120, sm: 150 },
            aspectRatio: "3 / 4",
            borderRadius: "20px",
            overflow: "hidden",
            backgroundColor: "#F2F7FB",
          }}
        >
          {(preview || profile.photo) && (
            <img
              src={preview || profile.photo}
              width="100%"
              height="100%"
              alt="Profile"
              style={{ objectFit: "cover" }}
            />
          )}
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 600, mb: 1 }}>Profile photo</Typography>
          <Button
            component="label"
            variant="outlined"
            sx={{ borderRadius: "24px", textTransform: "none" }}
          >
            {profile.photo ? "Change" : "Add photo"}
            <input
              ref={fileInputRef}
              hidden
              type="file"
              accept={ACCEPTED_IMAGE_TYPES.join(",")}
              disabled={loading}
              onChange={selectPhoto}
            />
          </Button>
        </Box>
      </Box>

      {cropSelection && (
        <ImageCropper
          open
          image={cropSelection.source}
          onCrop={uploadPhoto}
          onCancel={() => setCropSelection(null)}
          loading={loading}
        />
      )}

      <Typography mb={0.5} component="dt">
        Full name
      </Typography>
      <TextField
        fullWidth
        size="small"
        value={fullName}
        onChange={handleNameChange}
        sx={inputSx}
        inputProps={{ "aria-label": "Full name" }}
      />
    </Box>
  );
};

export default V2ProfileBasics;
